import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DropdownItem, { DropdownItemPropTypes } from './dropdown-item';
import useStateRef from '../../enhancers/custom-hook/use-state-ref';
import Button, { ButtonTypes, ButtonSizes } from '../button';
import SearchInput from '../search-input';
import KeyEventValues from '../../../constants/key-event-values';
import useTranslation from '../../../utils/translation-utils';

/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-props-no-spreading */
const DropdownContent = ({
  items, onItemClick, closeOnClick, avatarView, withSearch, withClearSelection, onSelectionClear,
  groupOptionsOnCategory, categoryKey, footerActions, renderCustomView, enableCustomView, header,
  handleClose, open, repositionPopper,
}) => {
  const [itemsList, setItemsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex, focusedIndexRef] = useStateRef(-1);
  const searchTermRef = useRef(null);
  const clearSelectionButtonRef = useRef(null);
  const dropdownItemRefs = useRef([]);
  const [getTranslation] = useTranslation();
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  const filterListItems = () => {
    const { filteredItemList, selectedIds } = items.reduce((acc, {
      id, label, selected, ...item
    }) => {
      const validItem = label && label.toLowerCase().includes(searchTerm.toLowerCase());

      if (validItem) {
        acc.filteredItemList.push({
          ...item, id, label, selected,
        });

        if (selected) {
          acc.selectedIds.push(id);
        }
      }
      return acc;
    }, { filteredItemList: [], selectedIds: [] });

    setItemsList(filteredItemList);
    setSelectedItemIds(selectedIds);
  };

  useEffect(() => {
    filterListItems();
  }, [items]);

  const getFocusableItems = () => {
    const updatedFocusableItems = [];
    if (searchTermRef.current) updatedFocusableItems.push(searchTermRef.current);
    if (dropdownItemRefs.current.length > 0) {
      updatedFocusableItems.push(...dropdownItemRefs.current
        .filter((dropdownItemRef) => dropdownItemRef));
    }
    if (clearSelectionButtonRef.current) {
      updatedFocusableItems.push(clearSelectionButtonRef.current);
    }
    return updatedFocusableItems;
  };

  const handleKeyDown = (event) => {
    const focusableItems = getFocusableItems();
    const currIndex = focusedIndexRef.current;
    let newIndex = currIndex;
    switch (event.key) {
      case KeyEventValues.ARROW_UP.key:
        event.preventDefault();
        newIndex = (newIndex - 1 + focusableItems.length) % focusableItems.length;
        break;
      case KeyEventValues.ARROW_DOWN.key:
        event.preventDefault();
        newIndex = (newIndex + 1) % focusableItems.length;
        break;
      case KeyEventValues.ENTER.key:
        event.preventDefault();
        focusableItems[newIndex]?.click();
        break;
      case KeyEventValues.ESCAPE.key:
        event.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
    setFocusedIndex(newIndex);
  };

  const handleItemHighlight = (targetId) => {
    const currIndex = (
      itemsList.findIndex((item) => item.id === targetId) + (withSearch ? 1 : 0)
    );
    if (currIndex !== focusedIndexRef.current) {
      setFocusedIndex(currIndex);
    }
  };

  const handleMouseLeave = () => {
    setFocusedIndex(-1);
  };

  useEffect(() => {
    const focusableItems = getFocusableItems();
    if ((withSearch && focusedIndex === 0)
      || (withClearSelection && focusedIndex === focusableItems.length - 1)) {
      focusableItems[focusedIndex].focus({ preventScroll: true });
    }
  }, [focusedIndex]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      setFocusedIndex(-1);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSearch = () => {
    filterListItems();
  };

  const handleItemClicked = (id, event) => {
    repositionPopper();
    if (onItemClick && typeof onItemClick === 'function') {
      onItemClick.call(null, id);
      setFocusedIndex(getFocusableItems().indexOf(event.currentTarget));
    }
    if (closeOnClick) {
      handleClose();
    }
  };

  const handleSearchClick = () => {
    setFocusedIndex(0);
  };

  const handleSelectionClear = () => {
    if (onSelectionClear && typeof onSelectionClear === 'function') {
      onSelectionClear();
      setFocusedIndex(getFocusableItems().indexOf(clearSelectionButtonRef));
    }
  };

  const getEmptyState = () => (
    <span className="ddc-empty-state">{getTranslation('NO_RESULT_FOUND')}</span>
  );

  const getItemsView = () => itemsList.map(({
    id, type, label, disabled, selected, renderIcon, image, helper, shortcut,
    renderIconButton, onIconButtonClick,
  }, index) => {
    const isDifferentCategory = () => itemsList[index][categoryKey]
      && itemsList[index - 1][categoryKey]
      && itemsList[index][categoryKey] !== itemsList[index - 1][categoryKey];

    const setDropdownItemRef = (element) => {
      dropdownItemRefs.current[index] = element;
    };

    return (
      <DropdownItem
        key={id}
        id={id}
        type={type}
        disabled={disabled}
        selected={selected}
        label={label}
        image={image}
        renderIcon={renderIcon}
        helper={helper}
        shortcut={shortcut}
        onClick={handleItemClicked}
        onItemHighlight={handleItemHighlight}
        highlighted={focusedIndex === index + (withSearch ? 1 : 0)}
        avatarView={avatarView}
        renderIconButton={renderIconButton}
        onIconButtonClick={onIconButtonClick}
        ref={setDropdownItemRef}
        showDivider={groupOptionsOnCategory
          && index > 0 && isDifferentCategory()}
        renderCustomView={renderCustomView}
        enableCustomView={enableCustomView}
      />
    );
  });

  const handleFooterAction = ({ onClick }) => () => {
    if (onClick && typeof onClick === 'function') {
      const closeDropdown = onClick.call(null) ?? false;
      if (closeDropdown) {
        handleClose();
      }
    }
  };

  return (
    <>
      {header}
      {withSearch && (
        <div className="dropdown-search" onClick={handleSearchClick}>
          <SearchInput
            value={searchTerm}
            onApply={handleSearch}
            onChange={(e) => setSearchTerm(e.target.value)}
            ref={searchTermRef}
          />
        </div>
      )}
      <div className="dropdown-items" onMouseLeave={handleMouseLeave}>
        {itemsList.length > 0 ? getItemsView() : getEmptyState()}
      </div>
      {withClearSelection && (
        <Button
          borderLess
          label={getTranslation('CLEAR_SELECTION')}
          size={ButtonSizes.SMALL}
          type={ButtonTypes.SECONDARY}
          className="dropdown-clear-selection"
          onClick={handleSelectionClear}
          ref={clearSelectionButtonRef}
          disabled={selectedItemIds.length === 0}
        />
      )}
      {footerActions.length > 0 && (
        footerActions.map((action) => (
          <Button
            {...action}
            key={action.id}
            className="dropdown-clear-selection"
            onClick={handleFooterAction(action)}
          />
        ))
      )}
    </>
  );
};

DropdownContent.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(DropdownItemPropTypes)),
  closeOnClick: PropTypes.bool,
  onItemClick: PropTypes.func,
  avatarView: PropTypes.bool,
  withSearch: PropTypes.bool,
  withClearSelection: PropTypes.bool,
  onSelectionClear: PropTypes.func,
  groupOptionsOnCategory: PropTypes.bool,
  categoryKey: PropTypes.string,
  footerActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ButtonTypes)),
    size: PropTypes.oneOf(Object.values(ButtonSizes)),
    renderIcon: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
    borderLess: PropTypes.bool,
  })),
  renderCustomView: PropTypes.func,
  enableCustomView: PropTypes.bool,
  handleClose: PropTypes.func,
  open: PropTypes.bool,
  repositionPopper: PropTypes.func,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

DropdownContent.defaultProps = {
  items: [],
  closeOnClick: true,
  onItemClick: () => {},
  avatarView: false,
  withSearch: false,
  withClearSelection: false,
  onSelectionClear: () => { },
  groupOptionsOnCategory: false,
  categoryKey: 'category',
  footerActions: [],
  renderCustomView: () => {},
  enableCustomView: false,
  handleClose: () => { },
  open: true,
  header: '',
  repositionPopper: () => {},
};

export default DropdownContent;
